import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { sessionStore, type Session } from "./session";

type SessionState = {
  session: Session | null;
  /** True until the stored token has been checked, so routes do not flicker. */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<Session>;
  signOut: () => Promise<void>;
  can: (permission: string) => boolean;
};

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    // load() throws rather than returning null when the API cannot be reached,
    // so a restart or a dropped connection does not look like a rejected
    // session. One retry covers a server coming back up; after that the
    // person lands on the login page, but their tokens are still stored, so
    // reloading once the API is back signs them straight in.
    async function restore(attempt = 0): Promise<void> {
      try {
        const loaded = await sessionStore.load();
        if (!cancelled) setSession(loaded);
      } catch (error) {
        if (attempt === 0 && !cancelled) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          if (!cancelled) return restore(1);
        }
        if (!cancelled) {
          console.warn("Could not reach the API to restore the session", error);
          setSession(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void restore();

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const next = await sessionStore.signIn(email, password);
    setSession(next);
    return next;
  }, []);

  const signOut = useCallback(async () => {
    await sessionStore.signOut();
    setSession(null);
  }, []);

  const value = useMemo<SessionState>(
    () => ({
      session,
      loading,
      signIn,
      signOut,
      can: (permission: string) =>
        session?.permissions.includes(permission) ?? false,
    }),
    [session, loading, signIn, signOut],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used inside a SessionProvider");
  }
  return context;
}
